import type { Entity } from '../ecs/entity'
import { PALETTE } from './palette'

/**
 * Manifest-based asset interface: entities reference an asset id; the
 * renderer resolves id → drawer. v1 drawers are polished procedural
 * primitives; a future sprite pack swaps entries to atlas frames without
 * touching game logic.
 */
export type PrimitiveDrawer = (ctx: CanvasRenderingContext2D, e: Entity, x: number, y: number, time: number) => void

export interface AssetEntry {
  kind: 'primitive'
  draw: PrimitiveDrawer
}

function shadow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.save()
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.beginPath()
  ctx.ellipse(x, y + r * 0.7, r * 0.95, r * 0.4, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function orb(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, base: string, hi: string): void {
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.15, x, y, r)
  g.addColorStop(0, hi)
  g.addColorStop(1, base)
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
}

function flashOverlay(ctx: CanvasRenderingContext2D, e: Entity, x: number, y: number, r: number): void {
  if (e.flash > 0) {
    ctx.save()
    ctx.globalAlpha = Math.min(1, e.flash * 9)
    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }
}

function nose(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, facing: number, color: string): void {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(facing)
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(r + 5, 0)
  ctx.lineTo(r - 3, -4.5)
  ctx.lineTo(r - 3, 4.5)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

const drawPlayer: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  // spawn/invuln blink
  if ((e.invuln ?? 0) > 0 && Math.floor(time * 12) % 2 === 0) ctx.globalAlpha = 0.45
  shadow(ctx, x, y, r)
  orb(ctx, x, y, r, '#8ea8bd', PALETTE.participant)
  // visor stripe toward facing
  nose(ctx, x, y, r, e.facing, PALETTE.participantAccent)
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  ctx.fillStyle = '#22303c'
  ctx.fillRect(-r * 0.15, -r * 0.55, r * 0.5, r * 1.1)
  ctx.fillStyle = PALETTE.participantAccent
  ctx.fillRect(r * 0.05, -r * 0.4, r * 0.28, r * 0.8)
  ctx.restore()
  flashOverlay(ctx, e, x, y, r)
  ctx.globalAlpha = 1
}

const drawHusk: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius * (1 + Math.sin(time * 7 + e.id) * 0.06)
  shadow(ctx, x, y, r)
  orb(ctx, x, y, r, '#a13317', PALETTE.husk)
  // eyes
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  ctx.fillStyle = '#2b0d05'
  ctx.beginPath()
  ctx.arc(r * 0.45, -r * 0.28, r * 0.16, 0, Math.PI * 2)
  ctx.arc(r * 0.45, r * 0.28, r * 0.16, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  flashOverlay(ctx, e, x, y, r)
}

const drawSentry: PrimitiveDrawer = (ctx, e, x, y, _time) => {
  const r = e.radius
  shadow(ctx, x, y, r)
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing + Math.PI / 4)
  const g = ctx.createLinearGradient(-r, -r, r, r)
  g.addColorStop(0, '#f06a56')
  g.addColorStop(1, '#8e211c')
  ctx.fillStyle = g
  ctx.fillRect(-r, -r, r * 2, r * 2)
  ctx.strokeStyle = '#4d0f0c'
  ctx.lineWidth = 2
  ctx.strokeRect(-r, -r, r * 2, r * 2)
  ctx.restore()
  // barrel
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  ctx.fillStyle = '#3a1210'
  ctx.fillRect(0, -2.5, r + 7, 5)
  ctx.restore()
  ctx.fillStyle = '#ffd9a0'
  ctx.beginPath()
  ctx.arc(x, y, r * 0.3, 0, Math.PI * 2)
  ctx.fill()
  flashOverlay(ctx, e, x, y, r)
}

const drawStatic: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  ctx.save()
  const flicker = 0.55 + Math.sin(time * 13 + e.id * 3) * 0.15
  ctx.globalAlpha = flicker
  const g = ctx.createRadialGradient(x, y, r * 0.1, x, y, r * 1.5)
  g.addColorStop(0, '#dfeaf4')
  g.addColorStop(0.6, PALETTE.static)
  g.addColorStop(1, 'rgba(159,180,200,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  // wavy ghost skirt
  ctx.arc(x, y, r, Math.PI, 0)
  const seg = 4
  for (let i = 0; i <= seg; i++) {
    const sx = x + r - (i * (r * 2)) / seg
    const sy = y + r * 0.8 + Math.sin(time * 6 + i * 2 + e.id) * 2.5
    ctx.lineTo(sx, sy)
  }
  ctx.closePath()
  ctx.fill()
  // hollow eyes
  ctx.globalAlpha = flicker + 0.2
  ctx.fillStyle = '#141c26'
  ctx.beginPath()
  ctx.arc(x - r * 0.32, y - r * 0.15, r * 0.17, 0, Math.PI * 2)
  ctx.arc(x + r * 0.32, y - r * 0.15, r * 0.17, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  flashOverlay(ctx, e, x, y, r)
}

const drawForeman: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  shadow(ctx, x, y, r)
  // aura ring
  ctx.save()
  ctx.globalAlpha = 0.14 + Math.sin(time * 3) * 0.05
  ctx.strokeStyle = PALETTE.foreman
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(x, y, 24 + Math.sin(time * 3) * 3, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
  // hex body
  ctx.save()
  ctx.translate(x, y)
  const g = ctx.createRadialGradient(-r * 0.3, -r * 0.4, r * 0.2, 0, 0, r)
  g.addColorStop(0, '#ffc46b')
  g.addColorStop(1, '#b45f0e')
  ctx.fillStyle = g
  ctx.beginPath()
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6
    const px = Math.cos(a) * r
    const py = Math.sin(a) * r
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
  }
  ctx.closePath()
  ctx.fill()
  // hazard band
  ctx.strokeStyle = '#3a2604'
  ctx.lineWidth = 3
  ctx.setLineDash([5, 4])
  ctx.beginPath()
  ctx.arc(0, 0, r * 0.62, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
  nose(ctx, x, y, r, e.facing, '#3a2604')
  flashOverlay(ctx, e, x, y, r)
}

const drawAuditor: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  ctx.save()
  // dread halo
  const halo = ctx.createRadialGradient(x, y, r * 0.3, x, y, r * 2.6)
  halo.addColorStop(0, 'rgba(138,77,255,0.35)')
  halo.addColorStop(1, 'rgba(138,77,255,0)')
  ctx.fillStyle = halo
  ctx.beginPath()
  ctx.arc(x, y, r * 2.6, 0, Math.PI * 2)
  ctx.fill()
  // obelisk silhouette
  const sway = Math.sin(time * 1.6) * 1.5
  ctx.translate(x + sway, y)
  const h = r * 2.4
  const g = ctx.createLinearGradient(0, -h, 0, r)
  g.addColorStop(0, '#120826')
  g.addColorStop(1, PALETTE.auditorBody)
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.moveTo(-r * 0.85, r)
  ctx.lineTo(-r * 0.55, -h * 0.75)
  ctx.lineTo(0, -h)
  ctx.lineTo(r * 0.55, -h * 0.75)
  ctx.lineTo(r * 0.85, r)
  ctx.closePath()
  ctx.fill()
  // seal — a slit of violet light
  ctx.fillStyle = PALETTE.auditor
  ctx.globalAlpha = 0.8 + Math.sin(time * 5) * 0.2
  ctx.fillRect(-r * 0.32, -h * 0.62, r * 0.64, 3)
  ctx.restore()
  if (e.flash > 0) {
    ctx.save()
    ctx.globalAlpha = Math.min(0.5, e.flash * 6)
    ctx.strokeStyle = PALETTE.auditor
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(x, y - r * 0.6, r * 1.4, 0, Math.PI * 2)
    ctx.stroke()
    ctx.restore()
  }
}

function genDrawer(coreColor: string, glyph: (ctx: CanvasRenderingContext2D, r: number) => void): PrimitiveDrawer {
  return (ctx, e, x, y, time) => {
    const r = e.radius
    const pulse = 0.5 + Math.sin((time / 1.2) * Math.PI * 2 + e.id) * 0.5
    shadow(ctx, x, y, r + 3)
    // pedestal
    ctx.save()
    ctx.translate(x, y)
    ctx.fillStyle = '#1c242e'
    ctx.strokeStyle = '#39434f'
    ctx.lineWidth = 2
    ctx.beginPath()
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8
      const px = Math.cos(a) * (r + 3)
      const py = Math.sin(a) * (r + 3)
      i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
    }
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // pulsing core — "alive and producing"
    const cr = r * (0.5 + pulse * 0.22)
    const g = ctx.createRadialGradient(0, 0, cr * 0.1, 0, 0, cr * 1.6)
    g.addColorStop(0, '#fff3e0')
    g.addColorStop(0.4, coreColor)
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.globalAlpha = 0.75 + pulse * 0.25
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(0, 0, cr * 1.6, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
    glyph(ctx, r)
    ctx.restore()
    // damage cracks
    if (e.hp !== undefined && e.maxHp !== undefined && e.hp < e.maxHp * 0.5) {
      ctx.save()
      ctx.strokeStyle = 'rgba(0,0,0,0.55)'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.moveTo(x - r * 0.6, y - r * 0.2)
      ctx.lineTo(x - r * 0.1, y + r * 0.3)
      ctx.lineTo(x + r * 0.5, y - r * 0.4)
      ctx.stroke()
      ctx.restore()
    }
    flashOverlay(ctx, e, x, y, r)
  }
}

function pickupDrawer(draw: (ctx: CanvasRenderingContext2D, t: number) => void): PrimitiveDrawer {
  return (ctx, e, x, y, time) => {
    const bob = Math.sin(time * 3 + e.id) * 2
    ctx.save()
    ctx.translate(x, y + bob)
    draw(ctx, time)
    ctx.restore()
  }
}

const drawProjPlayer: PrimitiveDrawer = (ctx, e, x, y, _time) => {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  const g = ctx.createLinearGradient(-14, 0, 6, 0)
  g.addColorStop(0, 'rgba(82,216,255,0)')
  g.addColorStop(1, PALETTE.projPlayer)
  ctx.fillStyle = g
  ctx.fillRect(-14, -2, 20, 4)
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(3, 0, 3.2, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

const drawProjEnemy: PrimitiveDrawer = (ctx, _e, x, y, time) => {
  const r = 4.5 + Math.sin(time * 20) * 0.6
  const g = ctx.createRadialGradient(x, y, 0.5, x, y, r * 1.8)
  g.addColorStop(0, '#ffe2b0')
  g.addColorStop(0.5, PALETTE.projEnemy)
  g.addColorStop(1, 'rgba(255,157,77,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r * 1.8, 0, Math.PI * 2)
  ctx.fill()
}

const drawCorpse: PrimitiveDrawer = (ctx, e, x, y, _time) => {
  ctx.save()
  ctx.globalAlpha = Math.min(0.5, (e.lifetime ?? 0) / 6)
  ctx.fillStyle = '#0c0f14'
  ctx.beginPath()
  ctx.ellipse(x, y, 9, 5, e.id % 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,106,61,0.25)'
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()
}

/** heat glow while telegraphing — mandatory readability, shared by all bosses */
function telegraphGlow(ctx: CanvasRenderingContext2D, e: Entity, x: number, y: number): void {
  if (e.boss?.stage !== 'telegraph') return
  const r = e.radius
  const t = e.boss.stageT
  const g = ctx.createRadialGradient(x, y, r * 0.5, x, y, r * 2.2)
  g.addColorStop(0, `rgba(255,79,94,${0.35 + Math.sin(t * 18) * 0.15})`)
  g.addColorStop(1, 'rgba(255,79,94,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r * 2.2, 0, Math.PI * 2)
  ctx.fill()
}

const drawBossWarden: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  shadow(ctx, x, y, r)
  telegraphGlow(ctx, e, x, y)
  // armored mass
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  const g = ctx.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.2, 0, 0, r)
  g.addColorStop(0, '#e2606b')
  g.addColorStop(1, '#5f1220')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, r, 0, Math.PI * 2)
  ctx.fill()
  // plating
  ctx.strokeStyle = '#2d0810'
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.arc(0, 0, r * 0.72, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(-r, 0)
  ctx.lineTo(r, 0)
  ctx.stroke()
  // crest / ram prow
  ctx.fillStyle = '#ffd9a0'
  ctx.beginPath()
  ctx.moveTo(r + 10, 0)
  ctx.lineTo(r - 6, -10)
  ctx.lineTo(r - 6, 10)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
  // broadcast crown light
  ctx.fillStyle = PALETTE.alarmRed
  ctx.globalAlpha = 0.6 + Math.sin(time * 6) * 0.4
  ctx.beginPath()
  ctx.arc(x, y - r - 6, 3.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalAlpha = 1
  flashOverlay(ctx, e, x, y, r)
}

/** the Overseer: warden family, amber-shifted, twin-ring/twin-eye motif */
const drawBossOverseer: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  shadow(ctx, x, y, r)
  telegraphGlow(ctx, e, x, y)
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  // heavier, squarer mass than the warden — an octagonal supervisor chassis
  const g = ctx.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.2, 0, 0, r)
  g.addColorStop(0, '#f0a05a')
  g.addColorStop(1, '#6e2c10')
  ctx.fillStyle = g
  ctx.beginPath()
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8
    const px = Math.cos(a) * r
    const py = Math.sin(a) * r
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
  }
  ctx.closePath()
  ctx.fill()
  // twin supervision rings
  ctx.strokeStyle = '#331104'
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.arc(0, 0, r * 0.78, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(0, 0, r * 0.5, 0, Math.PI * 2)
  ctx.stroke()
  // twin eyes, forward — it watches in stereo
  ctx.fillStyle = '#ffe9c2'
  ctx.beginPath()
  ctx.arc(r * 0.55, -r * 0.22, r * 0.14, 0, Math.PI * 2)
  ctx.arc(r * 0.55, r * 0.22, r * 0.14, 0, Math.PI * 2)
  ctx.fill()
  // blunt tow prow (squarer than the warden's ram)
  ctx.fillStyle = '#ffd9a0'
  ctx.fillRect(r - 2, -7, 10, 14)
  ctx.restore()
  // twin crown lights, counter-phased
  ctx.fillStyle = PALETTE.alertAmber
  ctx.globalAlpha = 0.6 + Math.sin(time * 5) * 0.4
  ctx.beginPath()
  ctx.arc(x - 7, y - r - 6, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalAlpha = 0.6 - Math.sin(time * 5) * 0.4
  ctx.beginPath()
  ctx.arc(x + 7, y - r - 6, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalAlpha = 1
  flashOverlay(ctx, e, x, y, r)
}

/** the Director: sharper, darker, red-shifted — executive authority */
const drawBossDirector: PrimitiveDrawer = (ctx, e, x, y, time) => {
  const r = e.radius
  shadow(ctx, x, y, r)
  telegraphGlow(ctx, e, x, y)
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(e.facing)
  // sharp eight-point star silhouette — all edges, no comfort
  const g = ctx.createRadialGradient(-r * 0.25, -r * 0.3, r * 0.15, 0, 0, r)
  g.addColorStop(0, '#7a1420')
  g.addColorStop(1, '#1c0308')
  ctx.fillStyle = g
  ctx.beginPath()
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2
    const rad = i % 2 === 0 ? r * 1.05 : r * 0.66
    const px = Math.cos(a) * rad
    const py = Math.sin(a) * rad
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
  }
  ctx.closePath()
  ctx.fill()
  ctx.strokeStyle = PALETTE.alarmRed
  ctx.lineWidth = 1.5
  ctx.stroke()
  // executive slit eye along the facing axis
  ctx.fillStyle = PALETTE.alarmRed
  ctx.globalAlpha = 0.85 + Math.sin(time * 7) * 0.15
  ctx.fillRect(r * 0.1, -2, r * 0.75, 4)
  ctx.globalAlpha = 1
  // authority chevrons across the core
  ctx.strokeStyle = '#ff8a94'
  ctx.lineWidth = 2
  ctx.beginPath()
  for (const off of [-r * 0.34, -r * 0.1, r * 0.14]) {
    ctx.moveTo(off - r * 0.16, -r * 0.3)
    ctx.lineTo(off + r * 0.06, 0)
    ctx.lineTo(off - r * 0.16, r * 0.3)
  }
  ctx.stroke()
  ctx.restore()
  // ON AIR light — a slow, deliberate blink
  ctx.fillStyle = PALETTE.alarmRed
  ctx.globalAlpha = Math.sin(time * 2.4) > 0 ? 0.95 : 0.25
  ctx.beginPath()
  ctx.arc(x, y - r - 7, 4, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalAlpha = 1
  flashOverlay(ctx, e, x, y, r)
}

export const ASSETS: Record<string, AssetEntry> = {
  player: { kind: 'primitive', draw: drawPlayer },
  enemy_husk: { kind: 'primitive', draw: drawHusk },
  enemy_sentry: { kind: 'primitive', draw: drawSentry },
  enemy_static: { kind: 'primitive', draw: drawStatic },
  enemy_foreman: { kind: 'primitive', draw: drawForeman },
  enemy_auditor: { kind: 'primitive', draw: drawAuditor },
  gen_husk: {
    kind: 'primitive',
    draw: genDrawer(PALETTE.husk, (ctx, r) => {
      ctx.fillStyle = '#2b0d05'
      ctx.beginPath()
      ctx.arc(-r * 0.2, 0, 2, 0, Math.PI * 2)
      ctx.arc(r * 0.2, 0, 2, 0, Math.PI * 2)
      ctx.fill()
    })
  },
  gen_sentry: {
    kind: 'primitive',
    draw: genDrawer(PALETTE.sentry, (ctx, r) => {
      ctx.strokeStyle = '#2b0d05'
      ctx.lineWidth = 2
      ctx.strokeRect(-r * 0.3, -r * 0.3, r * 0.6, r * 0.6)
    })
  },
  gen_static: {
    kind: 'primitive',
    draw: genDrawer(PALETTE.static, (ctx, r) => {
      ctx.strokeStyle = '#1a2430'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(-r * 0.35, 0)
      ctx.quadraticCurveTo(0, -r * 0.4, r * 0.35, 0)
      ctx.stroke()
    })
  },
  gen_foreman: {
    kind: 'primitive',
    draw: genDrawer(PALETTE.foreman, (ctx, r) => {
      ctx.strokeStyle = '#3a2604'
      ctx.lineWidth = 2
      ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.arc(0, 0, r * 0.35, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([])
    })
  },
  pickup_food: {
    kind: 'primitive',
    draw: pickupDrawer((ctx) => {
      // ration block with a nutrient cross
      const g = ctx.createLinearGradient(-7, -7, 7, 7)
      g.addColorStop(0, '#6ff0a0')
      g.addColorStop(1, '#1f9d53')
      ctx.fillStyle = g
      ctx.fillRect(-7, -6, 14, 12)
      ctx.strokeStyle = '#0d5a2c'
      ctx.lineWidth = 1.5
      ctx.strokeRect(-7, -6, 14, 12)
      ctx.fillStyle = '#eafff2'
      ctx.fillRect(-1.5, -4, 3, 8)
      ctx.fillRect(-4.5, -1.5, 9, 3)
    })
  },
  pickup_key: {
    kind: 'primitive',
    draw: pickupDrawer((ctx) => {
      ctx.strokeStyle = PALETTE.key
      ctx.fillStyle = PALETTE.key
      ctx.lineWidth = 2.5
      ctx.beginPath()
      ctx.arc(-4, 0, 3.5, 0, Math.PI * 2)
      ctx.stroke()
      ctx.fillRect(-1, -1.2, 9, 2.4)
      ctx.fillRect(5, 0, 2, 4)
      ctx.fillRect(8, 0, 2, 3)
    })
  },
  pickup_magic: {
    kind: 'primitive',
    draw: pickupDrawer((ctx, t) => {
      const g = ctx.createRadialGradient(0, 0, 1, 0, 0, 9)
      g.addColorStop(0, '#dffaff')
      g.addColorStop(0.6, PALETTE.magic)
      g.addColorStop(1, 'rgba(65,224,255,0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(0, 0, 9, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#0d5a6e'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(0, 0, 5.5, t, t + Math.PI * 1.4)
      ctx.stroke()
    })
  },
  pickup_treasure: {
    kind: 'primitive',
    draw: pickupDrawer((ctx, t) => {
      ctx.save()
      ctx.rotate(Math.sin(t * 2) * 0.15)
      const g = ctx.createLinearGradient(0, -7, 0, 7)
      g.addColorStop(0, '#fff7d6')
      g.addColorStop(1, '#d9a824')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.moveTo(0, -7)
      ctx.lineTo(6, -2)
      ctx.lineTo(4, 6)
      ctx.lineTo(-4, 6)
      ctx.lineTo(-6, -2)
      ctx.closePath()
      ctx.fill()
      ctx.strokeStyle = '#8a6a10'
      ctx.lineWidth = 1
      ctx.stroke()
      ctx.restore()
    })
  },
  pickup_archive: {
    kind: 'primitive',
    draw: pickupDrawer((ctx, t) => {
      // a filed record: pale document card, redaction bars, folded corner
      const g = ctx.createLinearGradient(-6, -8, 6, 8)
      g.addColorStop(0, '#f0f4f8')
      g.addColorStop(1, '#aebfce')
      ctx.fillStyle = g
      ctx.fillRect(-6, -8, 12, 16)
      ctx.strokeStyle = '#7c8ea0'
      ctx.lineWidth = 1
      ctx.strokeRect(-6, -8, 12, 16)
      ctx.fillStyle = '#7c8ea0'
      ctx.beginPath()
      ctx.moveTo(2, -8)
      ctx.lineTo(6, -8)
      ctx.lineTo(6, -4)
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = '#3d4c5c'
      ctx.fillRect(-4, -3, 8, 1.6)
      ctx.fillRect(-4, 0, 6, 1.6)
      ctx.fillRect(-4, 3, 7, 1.6)
      // classification pulse — reads as "restricted" from across the room
      ctx.save()
      ctx.globalAlpha = 0.35 + Math.sin(t * 4) * 0.25
      ctx.strokeStyle = PALETTE.signal
      ctx.strokeRect(-7.5, -9.5, 15, 19)
      ctx.restore()
    })
  },
  pickup_armament: {
    kind: 'primitive',
    draw: pickupDrawer((ctx, t) => {
      // issued ordnance crate: amber chevroned case, subtle ready-blink
      const g = ctx.createLinearGradient(-8, -6, 8, 6)
      g.addColorStop(0, '#4a3a12')
      g.addColorStop(1, '#2c220a')
      ctx.fillStyle = g
      ctx.fillRect(-8, -6, 16, 12)
      ctx.strokeStyle = '#ffd24a'
      ctx.lineWidth = 1.5
      ctx.strokeRect(-8, -6, 16, 12)
      ctx.globalAlpha = 0.6 + Math.sin(t * 4) * 0.3
      ctx.beginPath()
      ctx.moveTo(-4, 3)
      ctx.lineTo(0, -3)
      ctx.lineTo(4, 3)
      ctx.stroke()
      ctx.globalAlpha = 1
    })
  },
  proj_player: { kind: 'primitive', draw: drawProjPlayer },
  proj_enemy: { kind: 'primitive', draw: drawProjEnemy },
  corpse_husk: { kind: 'primitive', draw: drawCorpse },
  corpse_sentry: { kind: 'primitive', draw: drawCorpse },
  corpse_static: { kind: 'primitive', draw: drawCorpse },
  corpse_foreman: { kind: 'primitive', draw: drawCorpse },
  boss_warden: { kind: 'primitive', draw: drawBossWarden },
  boss_overseer: { kind: 'primitive', draw: drawBossOverseer },
  boss_director: { kind: 'primitive', draw: drawBossDirector }
}
